import React from 'react';

const RoiPerformanceTable = ({ data = [], model, theme = 'light', stockName }) => {
  const isDark = theme === 'dark';

  const normalizeDate = (value) => {
    try {
      if (!value) return null;
      if (value instanceof Date) return value;
      if (typeof value === 'string') return new Date(value);
      if (typeof value === 'object' && value.$date) return new Date(value.$date);
      return null;
    } catch {
      return null;
    }
  };

  const formatDate = (value) => {
    const d = normalizeDate(value);
    if (!d || isNaN(d.getTime())) return '-';
    return new Intl.DateTimeFormat('en-CA', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      timeZone: 'Europe/Berlin',
    }).format(d);
  };

  const fmtUsd = (num) => {
    const n = Number(num);
    if (!Number.isFinite(n)) return '-';
    return `$${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  // Filter by model if provided
  const rows = Array.isArray(data)
    ? [...data]
        .filter((r) => (model ? r?.model === model : true))
        // Exclude actions with zero price
        .filter((r) => {
          const pn = Number(r?.price);
          return !(Number.isFinite(pn) && pn === 0);
        })
        .sort((a, b) => {
          const da = normalizeDate(a?.date)?.getTime() || 0;
          const db = normalizeDate(b?.date)?.getTime() || 0;
          // Descending by date (newest first)
          return db - da;
        })
    : [];

  const initialInvestment = rows.length ? rows[0]?.initial_investment : data?.[0]?.initial_investment;
  const period = rows.length ? rows[0]?.period_months : data?.[0]?.period_months;

  return (
    <div className={`${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} p-3 rounded-lg border text-sm max-w-2xl mx-auto`}>
      <div>
        <div className="flex items-baseline justify-between">
          <h3 className="text-[14px] font-semibold tracking-wide">
            Backtested Trade History - {model || 'Model'} {stockName ? ` (${stockName})` : ''}
            <br />(up to {period} months)
          </h3>
          <div className="text-[12px] text-gray-600 dark:text-gray-400 inline-flex items-center gap-1">
            <span>Simulated Value: {fmtUsd(initialInvestment)}</span>

            <span className="relative inline-flex items-center group">
              <button
                type="button"
                aria-label="simulated value assumptions"
                className="ml-1 inline-flex h-4 w-4 items-center justify-center rounded-full border border-gray-300 text-[10px] font-semibold leading-none text-gray-600 hover:bg-gray-100 hover:text-gray-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700 dark:hover:text-gray-100"
              >
                i
              </button>
              <div className="pointer-events-none absolute right-0 top-full z-10 mt-2 w-56 rounded-md border border-gray-200 bg-white px-2 py-1 text-[12px] text-gray-700 opacity-0 shadow-sm transition-opacity group-hover:opacity-100 group-focus-within:opacity-100 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-200">
                Backtested on historical opens and closes. Assumes long-only positions, 0.2% commission per trade, no slippage, no taxes, and perfect execution at the listed price. Past performance is simulated and does not indicate future results.
              </div>
            </span>
          </div>
        </div>
        <p className="mt-1 mb-4 text-[11px] leading-snug text-gray-500 dark:text-gray-400">
          Hypothetical backtest of this model's past output on historical data. Not a live signal, a recommendation, or a forecast of future performance.
        </p>
      </div>

      <div className="overflow-x-auto mx-auto">
        <table className="w-full table-auto text-sm mx-auto">
          <thead>
            <tr className={isDark ? 'text-gray-200' : 'text-gray-800'}>
              <th className="text-center py-2 pr-3 font-semibold">Date</th>
              <th className="text-center py-2 pr-3 font-semibold">Action</th>
              <th className="text-center py-2 pr-3 font-semibold">Price</th>
              <th className="text-center py-2 pr-3 font-semibold">Investment Value</th>
              <th className="text-center py-2 font-semibold">ROI (%)</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td colSpan={5} className="py-4 text-center text-gray-500 dark:text-gray-400">
                  No ROI data{model ? ` for ${model}` : ''}.
                </td>
              </tr>
            ) : (
              rows.map((r, idx) => {
                const roiVal = Number(r?.metadata?.roi);
                const roiText = Number.isFinite(roiVal) ? `${roiVal.toFixed(2)}` : '-';
                const roiClass = Number.isFinite(roiVal)
                  ? roiVal > 0
                    ? 'text-green-600 dark:text-green-400'
                    : roiVal < 0
                      ? 'text-red-600 dark:text-red-400'
                      : 'text-gray-600 dark:text-gray-300'
                  : 'text-gray-600 dark:text-gray-300';
                return (
                  <tr key={idx} className={idx % 2 === 0 ? (isDark ? 'bg-gray-900/30' : 'bg-gray-50') : ''}>
                    <td className="py-2 pr-3">{formatDate(r?.date)}</td>
                    <td className="py-2 pr-3">{r?.action || '-'}</td>
                    <td className="py-2 pr-3">{fmtUsd(r?.price)}</td>
                    <td className="py-2 pr-3">{fmtUsd(r?.investment_value)}</td>
                    <td className={`py-2 font-medium ${roiClass}`}>{roiText}</td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default RoiPerformanceTable;
